import { PropsWithChildren, useRef } from 'react'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Calendar } from 'primereact/calendar'

import { Column, Row } from '../common/pure/FlexContainer'
import NavigationLink from '../side-bar/NavigationLink'
import { BasicOption } from '../../tools/settings'
import { CalendarField, NumberField } from '../common/pure/input-fields'
import t from '../../tools/t'

interface FilterProps {
    onReset?: () => void
    isSelected?: boolean
    className?: string
}

interface FilterOverlayProps extends PropsWithChildren, FilterProps {}
export default function FilterOverlay({ onReset, isSelected, children }: FilterOverlayProps) {
    return (
        <div className="custom-filter-overlay">
            <Row className="custom-filter-overlay__header" arrangement="spaced" alignment="center">
                <div className="custom-filter-overlay__title">{t('common_filter')}</div>
                <Button
                    disabled={!isSelected}
                    onClick={onReset}
                    className="custom-filter-overlay__reset"
                    severity="secondary"
                    size="small"
                    text
                >
                    {t('common_reset')}
                </Button>
            </Row>
            <Column gap={0.25} className="custom-filter-overlay__options">
                {children}
            </Column>
        </div>
    )
}

const FilterOverlayFieldColumn = ({ children }: PropsWithChildren) => (
    <Column gap={0.5} className="custom-filter-overlay__custom-field-column" children={children} />
)

export interface FilterOptionState<T> extends BasicOption {
    state?: T
}
interface EnumFilterOverlayProps extends FilterProps {
    options: FilterOptionState<boolean>[]
    onChange: (value: string) => void
}
export const EnumFilterOverlay = ({ options, onChange, ...props }: EnumFilterOverlayProps) => (
    <FilterOverlay {...props}>
        {options.map(({ label, value, state }) => (
            <NavigationLink key={value} onClick={() => onChange(value)}>
                <Checkbox checked={state ?? false} />
                {label}
            </NavigationLink>
        ))}
    </FilterOverlay>
)

interface NumericRangeFilterOverlayProps extends FilterProps {
    min?: number
    max?: number
    onChange: (min?: number, max?: number) => void
}
export const NumericRangeFilterOverlay = ({
    min,
    max,
    onChange,
    ...props
}: NumericRangeFilterOverlayProps) => (
    <FilterOverlay {...props}>
        <FilterOverlayFieldColumn>
            <NumberField
                value={min}
                unit={t('common_unit_mm')}
                placeholder={t('common_min')}
                onChange={(event) => onChange(event.value ?? undefined, max)}
            />
            <NumberField
                value={max}
                unit={t('common_unit_mm')}
                placeholder={t('common_max')}
                onChange={(event) => onChange(min, event.value ?? undefined)}
            />
        </FilterOverlayFieldColumn>
    </FilterOverlay>
)

interface DateRangeFilterOverlayProps extends FilterProps {
    range: Date[]
    onChange: (range: Date[]) => void
}
export const DateRangeFilterOverlay = ({
    range,
    onChange,
    ...props
}: DateRangeFilterOverlayProps) => {
    const ref = useRef<Calendar>(null)

    const changeDateRange = (dates: Date[]) => {
        onChange(dates)
        // Automatically close the calendar when a full range has been selected.
        if (dates.filter((d) => !!d).length >= 2) ref.current?.hide()
    }

    return (
        <FilterOverlay {...props}>
            <FilterOverlayFieldColumn>
                <CalendarField
                    ref={ref}
                    placeholder={t('search_filter_date_range')}
                    value={range}
                    onChange={(e) => changeDateRange(e.value as Date[])}
                    selectionMode="range"
                />
            </FilterOverlayFieldColumn>
        </FilterOverlay>
    )
}
